/**
 * =============================================================================
 * STAFF REPLY COMPOSER COMPONENT
 * =============================================================================
 *
 * Plain-text composer that lets an authorized admin post a staff-authored
 * reply from the question detail / reply moderation flow. Activates the
 * PR #315 backend route `POST /api/admin/questions/:questionId/replies`.
 *
 * Scope (deliberately minimal — `BL-STAFF-AUTHORING-001` Admin UI fast-follow):
 * plain text only. No rich text, attachments, product/document/video
 * references, RFI fields, author selector, staff-name/email field,
 * email/notification toggle, or solution checkbox. The client submits ONLY the
 * body; all authority/identity/state is server-derived.
 *
 * =============================================================================
 */

import React, { useState, useCallback } from 'react';
import { Box, Button, Flex, FlexItem, Text, Textarea } from '@bigcommerce/big-design';
import { CONTENT_VALIDATION } from '@find-my-part/types';

/**
 * Props for StaffReplyComposer.
 */
export interface StaffReplyComposerProps {
  /**
   * Submit handler. Resolves `true` on success (composer clears its text) and
   * `false` on failure (composer preserves its text). Receives the trimmed body.
   */
  onSubmit: (body: string) => Promise<boolean>;
  /** Whether a submit is in flight (drives the loading/disabled state). */
  isSubmitting: boolean;
  /** When true, the composer is disabled (e.g. token missing). */
  disabled?: boolean;
  /** Optional test ID prefix. */
  testId?: string;
}

/**
 * Plain-text staff reply composer.
 *
 * Client-side validation here is UX-only; the backend remains authoritative.
 * Submit is disabled for blank/whitespace-only input, input below
 * {@link CONTENT_VALIDATION.MIN_REPLY_LENGTH}, input above
 * {@link CONTENT_VALIDATION.MAX_REPLY_LENGTH}, while submitting, or when
 * `disabled`.
 */
export function StaffReplyComposer({
  onSubmit,
  isSubmitting,
  disabled = false,
  testId,
}: StaffReplyComposerProps): React.ReactElement {
  const [body, setBody] = useState('');

  const trimmedLength = body.trim().length;
  const isTooShort = trimmedLength < CONTENT_VALIDATION.MIN_REPLY_LENGTH;
  const isTooLong = body.length > CONTENT_VALIDATION.MAX_REPLY_LENGTH;
  const canSubmit = !isTooShort && !isTooLong && !isSubmitting && !disabled;

  const handleChange = useCallback(
    (event: React.ChangeEvent<HTMLTextAreaElement>): void => {
      setBody(event.target.value);
    },
    []
  );

  const handleSubmit = useCallback(async (): Promise<void> => {
    if (!canSubmit) return;
    // Clear only on success; preserve the draft on failure (resolved false OR
    // thrown) so the operator can retry without retyping. The hook's
    // createStaffReply is contractually non-throwing, but guard defensively so
    // a rejection never leaves an unhandled promise or wipes the draft.
    try {
      const succeeded = await onSubmit(body.trim());
      if (succeeded) {
        setBody('');
      }
    } catch {
      // Preserve the draft; the hook owns user-facing error messaging.
    }
  }, [canSubmit, onSubmit, body]);

  return (
    <Box
      marginBottom="medium"
      data-testid={testId}
    >
      <Text bold>
        Post a staff reply
      </Text>
      <Text color="secondary">
        Posts publicly as &quot;OGS Staff&quot; and is visible immediately. Plain text
        only — no email is sent.
      </Text>

      <Box marginTop="small">
        <Textarea
          label="Staff reply"
          value={body}
          onChange={handleChange}
          rows={3}
          maxLength={CONTENT_VALIDATION.MAX_REPLY_LENGTH}
          disabled={isSubmitting || disabled}
          placeholder="Write a reply to this question…"
          aria-label="Staff reply"
          data-testid={testId ? `${testId}-textarea` : undefined}
        />
      </Box>

      <Flex alignItems="center" justifyContent="space-between" marginTop="xSmall">
        <FlexItem>
          <Text color="secondary">
            {body.length} / {CONTENT_VALIDATION.MAX_REPLY_LENGTH.toLocaleString()} chars
          </Text>
        </FlexItem>
        <FlexItem>
          <Button
            variant="primary"
            onClick={handleSubmit}
            disabled={!canSubmit}
            isLoading={isSubmitting}
            data-testid={testId ? `${testId}-submit` : undefined}
          >
            Post reply
          </Button>
        </FlexItem>
      </Flex>
    </Box>
  );
}
